import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Container,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import unifiedApi from '../../utils/unifiedApi';
import useAutoSave from '../../hooks/useAutoSave';

const SLOTS_PER_PAGE = 10;

/**
 * SpareAvailabilityResponsePage - Anonymous page for spares to set their availability via token link
 */
const SpareAvailabilityResponsePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [data, setData] = useState(null);
  const [availabilities, setAvailabilities] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [changeCounter, setChangeCounter] = useState(0);
  const [slotPage, setSlotPage] = useState(0);
  const availabilitiesRef = useRef(availabilities);
  availabilitiesRef.current = availabilities;

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Load availability data from token
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await unifiedApi.getSpareAvailabilityByToken(token, userTimezone);
      
      if (!response) {
        setError('Invalid or expired link. Please request a new availability link.');
        return;
      }
      
      setData(response);
      
      // Initialize local availability state from response
      const initialAvailabilities = {};
      response.availabilities.forEach(a => {
        initialAvailabilities[a.spareTimeSlotId] = a.isAvailable;
      });
      setAvailabilities(initialAvailabilities);
    } catch (err) {
      console.error('Error loading availability data:', err);
      if (err.message?.includes('400') || err.message?.includes('expired')) {
        setError('This link has expired or is invalid. Please request a new availability link.');
      } else {
        setError('Failed to load availability data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, userTimezone]);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, loadData]);

  // Auto-save callback — reads availabilities from ref to avoid stale closures
  const performSave = useCallback(async () => {
    const availabilityList = Object.entries(availabilitiesRef.current).map(([slotId, isAvailable]) => ({
      spareTimeSlotId: parseInt(slotId),
      isAvailable
    }));
    await unifiedApi.submitSpareAvailabilityByToken(token, availabilityList);
  }, [token]);

  const { saveStatus, triggerSave } = useAutoSave(performSave, hasChanges, {
    onSuccess: () => {
      setSuccess('Your availability has been saved.');
      setHasChanges(false);
    },
    onError: (err) => {
      console.error('Error submitting availability:', err);
      if (err.message?.includes('400') || err.message?.includes('expired')) {
        setError('This link has expired. Please request a new availability link.');
      } else {
        setError('Failed to save availability. Please try again.');
      }
    },
  }, changeCounter);

  // Handle availability toggle
  const handleToggle = (timeSlotId, newValue) => {
    setAvailabilities(prev => ({
      ...prev,
      [timeSlotId]: newValue
    }));
    setHasChanges(true);
    setChangeCounter(c => c + 1);
    setSuccess(null);
  };

  // Format date/time for display
  const formatTimeSlot = (startTime) => {
    const date = new Date(startTime);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    });
    return { dateStr, timeStr };
  };

  // Render availability toggle buttons
  const renderToggle = (timeSlotId, currentValue) => {
    return (
      <ToggleButtonGroup
        value={currentValue}
        exclusive
        onChange={(e, newValue) => {
          // Only update if a button was clicked (newValue is not null)
          if (newValue !== null) {
            handleToggle(timeSlotId, newValue);
          }
        }}
        size="small"
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        <ToggleButton 
          value={true} 
          sx={{ 
            flex: { xs: 1, sm: 'initial' },
            '&.Mui-selected': { 
              backgroundColor: 'success.light',
              color: 'success.contrastText',
              '&:hover': { backgroundColor: 'success.main' }
            }
          }}
        >
          <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
          Yes
        </ToggleButton>
        <ToggleButton 
          value={false}
          sx={{ 
            flex: { xs: 1, sm: 'initial' },
            '&.Mui-selected': { 
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              '&:hover': { backgroundColor: 'error.main' }
            }
          }}
        >
          <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
          No
        </ToggleButton>
      </ToggleButtonGroup>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Loading your availability...</Typography>
        </Paper>
      </Container>
    );
  }

  // Error state (no data)
  if (error && !data) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Contact your league organizer to get a new availability link.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4, px: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Close button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            startIcon={<CloseIcon />}
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
            size="small"
            color="inherit"
          >
            Close
          </Button>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Spare Availability
          </Typography>
          <Typography variant="h6" color="primary">
            {data.memberName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.leagueName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Link expires: {new Date(data.expirationDate).toLocaleDateString()}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Select your availability for each game time below. Click &quot;Yes&quot; if you can play, &quot;No&quot; if you cannot.
        </Alert>

        {/* Time slots list */}
        {data.availabilities.length === 0 ? (
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No upcoming game times available.
          </Typography>
        ) : (() => {
          const now = new Date();
          const futureAvailabilities = data.availabilities.filter(a => new Date(a.timeSlot.startTime) >= now);
          const totalSlots = futureAvailabilities.length;

          if (totalSlots === 0) {
            return (
              <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                No upcoming game times available.
              </Typography>
            );
          }

          const totalPages = Math.ceil(totalSlots / SLOTS_PER_PAGE);
          const pagedAvailabilities = futureAvailabilities.slice(slotPage * SLOTS_PER_PAGE, (slotPage + 1) * SLOTS_PER_PAGE);

          return (
          <>
            {/* Pagination controls */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Button
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  onClick={() => setSlotPage(p => Math.max(0, p - 1))}
                  disabled={slotPage === 0}
                >
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {slotPage * SLOTS_PER_PAGE + 1}&ndash;{Math.min((slotPage + 1) * SLOTS_PER_PAGE, totalSlots)} of {totalSlots}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => setSlotPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={slotPage >= totalPages - 1}
                >
                  Next
                </Button>
              </Box>
            )}

            <List>
              {pagedAvailabilities.map((availability, index) => {
                const { dateStr, timeStr } = formatTimeSlot(availability.timeSlot.startTime);
                const currentValue = availabilities[availability.spareTimeSlotId];
                
                return (
                  <React.Fragment key={availability.spareTimeSlotId}>
                    <ListItem
                      sx={{
                        py: 2,
                        px: { xs: 0, sm: 2 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1.5, sm: 2 }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="medium">
                            {dateStr}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {timeStr}
                          </Typography>
                        }
                        sx={{ flex: 1, m: 0 }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: { xs: '100%', sm: 'auto' },
                          flexShrink: 0
                        }}
                      >
                        {currentValue === null || currentValue === undefined ? (
                          <HelpOutlineIcon sx={{ color: 'warning.main' }} />
                        ) : null}
                        {renderToggle(availability.spareTimeSlotId, currentValue)}
                      </Box>
                    </ListItem>
                    {index < pagedAvailabilities.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>

            {/* Pagination controls (bottom) */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  onClick={() => setSlotPage(p => Math.max(0, p - 1))}
                  disabled={slotPage === 0}
                >
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {slotPage * SLOTS_PER_PAGE + 1}&ndash;{Math.min((slotPage + 1) * SLOTS_PER_PAGE, totalSlots)} of {totalSlots}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => setSlotPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={slotPage >= totalPages - 1}
                >
                  Next
                </Button>
              </Box>
            )}

            {/* Auto-save status and fallback save button */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              {saveStatus === 'saving' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Saving...</Typography>
                </Box>
              )}
              {saveStatus === 'saved' && !hasChanges && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CloudDoneIcon fontSize="small" color="success" />
                  <Typography variant="body2" color="success.main">All changes saved</Typography>
                </Box>
              )}
              {saveStatus === 'error' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudOffIcon fontSize="small" color="error" />
                    <Typography variant="body2" color="error.main">Failed to save</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={triggerSave}
                  >
                    Retry Save
                  </Button>
                </Box>
              )}
              {saveStatus === 'idle' && !hasChanges && !success && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Changes are saved automatically
                </Typography>
              )}
            </Box>
          </>
          );
        })()}
      </Paper>
    </Container>
  );
};

export default SpareAvailabilityResponsePage;
